import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Slide, Stack, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";

import { useLinkedParts, type LinkedPart } from "../../services/PartService";
import PartMatchCard from "../../dashboard/PartMatchCard";

interface LinkedPartsTabProps {
  dialogueId: number | undefined;
}

// Tunes the slide-in motion. STAGGER_MS is the gap between successive cards
// starting their slide; SLIDE_MS is the duration of each individual slide.
const STAGGER_MS = 175;
const SLIDE_MS = 320;

type AnimState = {
  // False until we've reconciled the first defined-data snapshot for the
  // current dialogue. The first tick goes through the cold-load branch
  // (everything visible instantly, no animation); subsequent ticks animate.
  initialized: boolean;
  // Part IDs currently visible (in=true on their Slide wrapper).
  visibleIds: Set<number>;
  // Part IDs queued to slide in next, in the order they should appear.
  pendingIds: number[];
};

const INITIAL_ANIM_STATE: AnimState = {
  initialized: false,
  visibleIds: new Set(),
  pendingIds: [],
};

/**
 * Sidebar tab listing the parts pinned to this dialogue:
 *
 * - **Search dialogue** — parts surfaced via search tool calls.
 * - **Comparison dialogue** — the two parts being compared.
 * - **Existing-part edit run** — the part being refined.
 *
 * Refresh is event-driven: `PartsAISidebar` invalidates `LINKED_PARTS_QUERY_KEY`
 * whenever a search/clear tool call completes, triggering a refetch via `useLinkedParts`.
 *
 * Newly-arrived parts slide in from the right one-by-one.
 * The very first cold load appears instantly.
 */
const LinkedPartsTab = ({ dialogueId }: LinkedPartsTabProps) => {
  const history = useHistory();

  const { data, isLoading, isError } = useLinkedParts(dialogueId);

  const [animState, setAnimState] = useState<AnimState>(INITIAL_ANIM_STATE);

  // Active stagger timer. Held in a ref so re-renders that append to the
  // tail of ``pendingIds`` don't yank the in-flight timer for the head.
  const drainTimerRef = useRef<number | null>(null);

  // Reset state whenever the user navigates to a different dialogue. Also
  // cancel any pending drain timer so a tick that fires after navigation
  // can't leak a stale part ID into the new dialogue's visibleIds.
  useEffect(() => {
    if (drainTimerRef.current !== null) {
      window.clearTimeout(drainTimerRef.current);
      drainTimerRef.current = null;
    }
    setAnimState(INITIAL_ANIM_STATE);
  }, [dialogueId]);

  // Reconcile animation state with the latest data: prune removed IDs, queue
  // newly-arrived IDs for staggered reveal.
  useEffect(() => {
    if (data === undefined) {
      return;
    }
    const currentIds = new Set(data.map((d) => d.part.id));
    const orderedIds = data.map((d) => d.part.id);

    setAnimState((prev) => {
      // Cold load: first defined-data snapshot for this dialogue. Drop
      // everything straight into visibleIds with no animation so existing
      // pinned parts don't all slide in when the tab is opened.
      if (!prev.initialized) {
        return {
          initialized: true,
          visibleIds: new Set(currentIds),
          pendingIds: [],
        };
      }

      // Warm reconcile. Carry over previous IDs that are still present,
      // and queue anything we haven't shown or queued yet.
      const nextVisible = new Set<number>();
      prev.visibleIds.forEach((id) => {
        if (currentIds.has(id)) nextVisible.add(id);
      });
      const nextPending = prev.pendingIds.filter((id) => currentIds.has(id));

      const pendingSet = new Set(nextPending);
      const addedNow = new Set<number>();
      const newlyAppeared: number[] = [];
      for (const id of orderedIds) {
        if (!nextVisible.has(id) && !pendingSet.has(id) && !addedNow.has(id)) {
          newlyAppeared.push(id);
          addedNow.add(id);
        }
      }

      if (newlyAppeared.length === 0) {
        return {
          initialized: true,
          visibleIds: nextVisible,
          pendingIds: nextPending,
        };
      }
      return {
        initialized: true,
        visibleIds: nextVisible,
        pendingIds: [...nextPending, ...newlyAppeared],
      };
    });
  }, [data]);

  // Drain ``pendingIds`` one ID at a time. The effect re-runs whenever
  // ``pendingIds`` changes — including when a refetch appends to the
  // tail. To avoid restarting the stagger clock on every append we hold
  // the in-flight timer in a ref and skip scheduling when one is already
  // counting down toward revealing the current head. When the timer
  // fires, the setter guards against a head swap (dialogue reset, or the
  // reconcile effect dropping a removed ID) so a stale tick can't leak.
  useEffect(() => {
    if (animState.pendingIds.length === 0) {
      return;
    }
    if (drainTimerRef.current !== null) {
      return;
    }
    const nextId = animState.pendingIds[0];
    drainTimerRef.current = window.setTimeout(() => {
      drainTimerRef.current = null;
      setAnimState((prev) => {
        if (prev.pendingIds[0] !== nextId) {
          return prev;
        }
        return {
          initialized: prev.initialized,
          visibleIds: new Set([...prev.visibleIds, nextId]),
          pendingIds: prev.pendingIds.slice(1),
        };
      });
    }, STAGGER_MS);
  }, [animState.pendingIds]);

  // Cancel any in-flight stagger timer when the component unmounts so the
  // setter callback can't fire against an unmounted instance.
  useEffect(() => {
    return () => {
      if (drainTimerRef.current !== null) {
        window.clearTimeout(drainTimerRef.current);
        drainTimerRef.current = null;
      }
    };
  }, []);

  const handleClick = (partId: number) => {
    history.push(`/parts/detail/${partId}`);
  };

  if (!dialogueId) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary">
          Couldn't load linked parts. Try refreshing.
        </Typography>
      </Box>
    );
  }

  const items: LinkedPart[] = data ?? [];

  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary">
          No parts linked yet — use the search tools (or open a comparison) to
          pin parts to this conversation.
        </Typography>
      </Box>
    );
  }

  return (
    // Hide overflow so the off-screen Slide children don't trigger a
    // horizontal scrollbar mid-animation.
    <Stack spacing={1.5} sx={{ p: 2, overflowX: "hidden" }}>
      {items.map((item) => (
        <Slide
          key={item.part.id}
          direction="left"
          in={animState.visibleIds.has(item.part.id)}
          // mountOnEnter — pending items aren't in the DOM yet, so the
          // Stack "grows" as each card slides in rather than reserving
          // empty slots up-front.
          mountOnEnter
          timeout={SLIDE_MS}
        >
          {/* Box wrapper so Slide has a single ref-forwarding element to
              animate, regardless of how `PartMatchCard` composes internally. */}
          <Box>
            <PartMatchCard
              match={{
                part_id: item.part.id,
                name: item.part.name,
                part_type: item.part.part_type,
                manufacturer: item.part.manufacturer,
                description: item.part.description,
                distance: item.distance,
              }}
              onClick={handleClick}
              showPhoto
            />
          </Box>
        </Slide>
      ))}
    </Stack>
  );
};

export default LinkedPartsTab;
